import React, { useEffect, useState } from "react";
import ReportTable from "../primitives/GetReportDisplay";
import Parse from "parse";
import axios from "axios";
import Title from "../components/Title";
import PageNotFound from "./PageNotFound";
import { useNavigate } from "react-router-dom";
import CommonDeleteModal from "../components/CommonDeleteModal";
import { useMessage } from "../hook/showMessage";

const UserList = () => {
  const navigate = useNavigate();
  const { showMessage } = useMessage();
  const [List, setList] = useState([]);
  const [isLoader, setIsLoader] = useState(true);
  const [isNextRecord, setIsNextRecord] = useState(false);
  const [isMoreDocs, setIsMoreDocs] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  // Table headings
  const heading = ["Sr.No", "Name", "Email", "Role"];

  // Table actions (View & Delete)
  const actions = [
    {
      btnLabel: "View",
      btnColor: "#4bd396",
      textColor: "white",
      btnIcon: "fa fa-eye",
      redirectUrl: "viewUser" // navigate to view page
    },
    {
      btnLabel: "Edit",
      btnColor: "#00c9d5",
      textColor: "white",
      btnIcon: "fa fa-pencil",
      selector: "reactourThird",
      message:
        "Use the ‘Edit’ button to add signer roles, modify fields, and update your template. Changes will apply to all future documents created from this template but won’t affect existing documents."
    },
    {
      btnLabel: "Delete",
      btnColor: "#f55a42",
      textColor: "white",
      btnIcon: "fa-solid fa-trash",
      message: "Use the ‘Delete’ button to delete the User"
    }
  ];

  const handleViewUser = (user) => {
    navigate(`/users/view/${user.id}`, { state: { user, action: "View" } });
  };

  const handleEditUser = (user) => {
    navigate(`/users/edit/${user.id}`, { state: { user, action: "Edit" } });
  };

  const handleDeleteClick = (user) => {
    setSelectedUser(user?.id);
    setIsDeleteOpen(true);
  };

  const handleDeleteUser = async () => {
    try {
      const res = await Parse.Cloud.run("deleteUserCompletely", {
        userId: selectedUser
      });
      setIsDeleteOpen(false);
      if (res?.success) {
        showMessage({
          type: "success",
          title: "Deleted",
          message: "User deleted successfully"
        });
        // Remove user from list
        setList((prev) => prev.filter((u) => u.id !== selectedUser));
      } else {
        throw new Error(res?.message || "Failed to delete user");
      }
    } catch (err) {
      console.error(err);
      setIsDeleteOpen(false);
      showMessage({
        type: "error",
        title: "Error",
        message: err?.message || "Failed to delete user"
      });
    } finally {
      setIsDeleteOpen(false);
      setSelectedUser(null);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteOpen(false);
    setSelectedUser(null);
  };

  const abortController = new AbortController();
  const docPerPage = 10;

  // Fetch user list from Parse Cloud Function
  const getUserListData = async (skipUserRecord = 0, limit = 10) => {
    try {
      const currentUser = Parse.User.current()?.id;
      if (!currentUser) {
        console.error("No user logged in");
        setIsLoader(false);
        return;
      }

      const headers = {
        "Content-Type": "application/json",
        "X-Parse-Application-Id": localStorage.getItem("parseAppId"),
        sessiontoken: localStorage.getItem("accesstoken")
      };

      const params = { skip: skipUserRecord, limit: limit };
      const url = `${localStorage.getItem("baseUrl")}/functions/getUsers`;

      const res = await axios.post(url, params, {
        headers,
        signal: abortController.signal
      });

      const result = res.data?.result || [];
      if (result.length === docPerPage) {
        setIsMoreDocs(true);
      } else {
        setIsMoreDocs(false);
      }

      // Add serial number for display
      const formattedResult = result.map((user, index) => ({
        ...user,
        srNo: skipUserRecord + index + 1
      }));

      setIsNextRecord(false);
      setList((prev) =>
        prev.length > 0 ? [...prev, ...formattedResult] : formattedResult
      );

      setIsLoader(false);
    } catch (err) {
      const isCancel = axios.isCancel(err);
      if (!isCancel) {
        console.error("Error fetching user list:", err);
        setIsLoader(false);
      }
    }
  };

  // Initial load
  useEffect(() => {
    setList([]);
    setIsLoader(true);
    getUserListData();

    return () => {
      abortController.abort();
    };
    // eslint-disable-next-line
  }, []);

  // Pagination: load more records
  useEffect(() => {
    if (isNextRecord) {
      getUserListData(List.length, docPerPage);
    }
    // eslint-disable-next-line
  }, [isNextRecord]);

  return (
    <>
      <Title title="Users" />
      {isLoader ? (
        <div
          style={{
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <div
            style={{
              fontSize: "45px",
              color: "#3dd3e0"
            }}
            className="loader-37"
          ></div>
        </div>
      ) : (
        <>
          {List.length > 0 ? (
            <ReportTable
              ReportName="Users"
              List={List}
              setList={setList}
              actions={actions}
              heading={heading}
              setIsNextRecord={setIsNextRecord}
              isMoreDocs={isMoreDocs}
              docPerPage={docPerPage}
              onView={handleViewUser}
              onEdit={handleEditUser}
              onDelete={handleDeleteClick}
            />
          ) : (
            <PageNotFound prefix={"Users"} />
          )}
        </>
      )}
      <CommonDeleteModal
        isOpen={isDeleteOpen}
        message="Are you sure you want to delete this User?"
        onConfirm={() => handleDeleteUser()}
        onCancel={() => handleCancelDelete()}
      />
    </>
  );
};

export default UserList;
